import React, { useMemo, useState } from "react";
import { freeOver } from "@/lib/storenav";
import { hasPriceRange } from "@/lib/variants";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Star, Truck, ShieldCheck, BadgeCheck, Headphones, ArrowRight, Quote, Tag, ChevronRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StoreShell from "@/components/StoreShell";
import { inr, rating, reviews, shade, hexA } from "@/lib/storeutils";
import { homeSets } from "@/lib/homesets";
import { ManagedReviews, ManagedInstagram } from "@/components/storefront/HomeSections";
import PharmacyHome from "./PharmacyHome";
import MediHome from "./MediHome";
import BookHome from "./BookHome";
import InkHome from "./InkHome";
import VerdantHome from "./VerdantHome";
import AromaHome from "./AromaHome";
import MarketHome from "./MarketHome";
import MeatHome from "./MeatHome";
import ArtisanHome from "./ArtisanHome";

export default function Storefront() {
  const { theme } = useStore();
  if (theme?.layout === "pharmacy") return <PharmacyHome />;
  if (theme?.layout === "medi") return <MediHome />;
  if (theme?.layout === "books") return <BookHome />;
  if (theme?.layout === "bookish") return <InkHome />;
  if (theme?.layout === "verdant") return <VerdantHome />;
  if (theme?.layout === "aroma") return <AromaHome />;
  if (theme?.layout === "market") return <MarketHome />;
  if (theme?.layout === "meatverse") return <MeatHome />;
  if (theme?.layout === "artisan") return <ArtisanHome />;
  return <ClassicStorefront />;
}

function ClassicStorefront() {
  const s = useStore();
  const cart = useCart();
  const [activeCat, setActiveCat] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => s.products.filter((p) =>
    (activeCat === "All" || p.category === activeCat) && p.name.toLowerCase().includes(query.toLowerCase())
  ), [s.products, activeCat, query]);

  if (s.loading) return <Splash />;
  if (s.error) return <ErrorView error={s.error} />;

  const { c, font, border, muted, cardShadow, slug, dark } = s;
  const best = s.products.filter((p) => p.featured);
  const bestList = (best.length ? best : s.products).slice(0, 8);
  // three shelves with different products: new arrivals, featured, on sale (see lib/homesets.js)
  const sets = homeSets(s.products, { fresh: 4, featured: 4, sale: 4 });
  const shelved = new Set([...sets.fresh, ...sets.featured, ...sets.sale].map((p) => p.id));
  const restList = s.products.filter((p) => !shelved.has(p.id)).slice(0, 8);      // "Our Products" starts with what the shelves don't show
  const defaultGrid = restList.length >= 2 ? restList : bestList;
  const cats = ["All", ...s.categories];
  const catTiles = s.categories.map((cat) => ({ name: cat, image: s.products.find((p) => p.category === cat)?.image, count: s.products.filter((p) => p.category === cat).length }));


  return (
    <StoreShell>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-8">
        <div className="rounded-3xl overflow-hidden grid md:grid-cols-2 items-stretch" style={{ background: `linear-gradient(115deg, ${c.primary}, ${shade(c.primary, -18)})` }}>
          <div className="p-10 md:p-14 flex flex-col justify-center">
            <span className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full w-fit" style={{ background: "rgba(255,255,255,0.18)", color: "#fff" }}><BadgeCheck size={13} /> {s.store.business_category}</span>
            <h1 className="text-3xl md:text-5xl font-extrabold mt-5 leading-[1.08]" style={{ color: "#fff", fontFamily: font }}>{s.store.tagline || `Welcome to ${s.store.name}`}</h1>
            <p className="mt-4 text-base max-w-md" style={{ color: "rgba(255,255,255,0.85)" }}>Discover our handpicked collection at {s.store.name}. Quality products, fast delivery, unbeatable prices.</p>
            <a href="#shop" className="mt-7 w-fit px-7 py-3 rounded-full font-bold flex items-center gap-2 transition-transform hover:scale-[1.03]" style={{ background: c.accent, color: "#fff" }} data-testid="storefront-shop-collection">Shop Collection <ArrowRight size={17} /></a>
          </div>
          <div className="relative min-h-[260px] md:min-h-0" style={{ background: shade(c.primary, -25) }}>
            {bestList[0]?.image && <img src={bestList[0].image} alt="featured" className="absolute inset-0 w-full h-full object-cover opacity-90" />}
            <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${shade(c.primary, -18)}, transparent 40%)` }} />
          </div>
        </div>
      </section>

      <FeatureStrip s={s} />

      {/* Categories */}
      {catTiles.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 pb-4">
          <SectionHead c={c} font={font} eyebrow="Top Categories" title="Shop by Category" />
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mt-6">
            {catTiles.map((cat) => (
              <button key={cat.name} onClick={() => { setActiveCat(cat.name); document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }} className="flex flex-col items-center gap-2 group" data-testid={`storefront-cat-tile-${cat.name}`}>
                <div className="w-full aspect-square rounded-2xl overflow-hidden" style={{ background: hexA(c.primary, 0.08), border: `1px solid ${border}` }}>
                  {cat.image ? <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : null}
                </div>
                <span className="text-xs font-semibold text-center" style={{ fontFamily: font }}>{cat.name}</span>
                <span className="text-[10px]" style={{ color: muted }}>{cat.count} items</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <PromoStrip s={s} />

      <HomeShelf s={s} cart={cart} id="new-arrivals" eyebrow="Just In" title="New Arrivals" items={sets.fresh} />
      <HomeShelf s={s} cart={cart} id="featured" eyebrow="Handpicked For You" title="Featured Products" items={sets.featured} />
      <HomeShelf s={s} cart={cart} id="deals" eyebrow="Great Offers" title="On Sale" items={sets.sale} />

      {/* Products */}
      <section id="shop" className="max-w-6xl mx-auto px-5 pt-2 pb-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <SectionHead c={c} font={font} eyebrow="For The Best Products" title={activeCat === "All" ? "Our Products" : activeCat} />
          <div className="flex items-center gap-2 rounded-full px-4 py-2 w-64 border" style={{ borderColor: border }}>
            <Search size={15} style={{ color: muted }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products…" data-testid="storefront-search" className="bg-transparent outline-none text-sm w-full" style={{ color: c.text }} />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap mt-5 mb-6">
          {cats.map((cat) => (
            <button key={cat} onClick={() => setActiveCat(cat)} data-testid={`storefront-cat-${cat}`} className="px-4 py-1.5 rounded-full text-sm font-medium transition-all"
              style={activeCat === cat ? { background: c.primary, color: "#fff" } : { background: c.surface, color: c.text, border: `1px solid ${border}` }}>{cat}</button>
          ))}
        </div>
        {filtered.length === 0 ? <div className="text-center py-16" style={{ color: muted }}>No products found.</div> : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="storefront-products">
            {(activeCat === "All" && !query ? defaultGrid : filtered).map((p) => <ProductCard key={p.id} p={p} s={s} cart={cart} />)}
          </div>
        )}
      </section>

      <ManagedReviews s={s} layout="retail" fallback={(
      <section className="max-w-6xl mx-auto px-5 py-10">
        <SectionHead c={c} font={font} eyebrow="Testimonials" title="What Our People Say" />
        <div className="grid md:grid-cols-3 gap-5 mt-6">
          {[["Priya M.", "Love the quality and the fast delivery. Will shop again!"], ["Arjun K.", "Great prices and the checkout was super smooth."], ["Sneha R.", "Beautiful products and wonderful support. 5 stars!"]].map(([name, text]) => (
            <div key={name} className="rounded-2xl p-6" style={{ background: c.surface, border: `1px solid ${border}`, boxShadow: cardShadow }}>
              <Quote size={22} style={{ color: c.accent }} />
              <p className="mt-3 text-sm" style={{ color: muted }}>{text}</p>
              <div className="flex items-center gap-1 mt-4">{[...Array(5)].map((_, i) => <Star key={i} size={13} fill={c.accent} color={c.accent} />)}</div>
              <div className="mt-3 font-bold text-sm" style={{ fontFamily: font }}>{name}</div>
            </div>
          ))}
        </div>
      </section>
      )} />
      <ManagedInstagram s={s} layout="retail" />
    </StoreShell>
  );
}

function HomeShelf({ s, cart, id, eyebrow, title, items }) {
  if (!items.length) return null;
  return (
    <section id={id} className="max-w-6xl mx-auto px-5 py-6" data-testid={`home-shelf-${id}`}>
      <SectionHead c={s.c} font={s.font} eyebrow={eyebrow} title={title} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
        {items.map((p) => <ProductCard key={p.id} p={p} s={s} cart={cart} />)}
      </div>
    </section>
  );
}

function FeatureStrip({ s }) {
  const { c, font, border, muted, cardShadow } = s;
  return (
<section className="max-w-6xl mx-auto px-5 py-10">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[[Truck, freeOver(s.store) > 0 ? "Free Shipping" : "Reliable Delivery", freeOver(s.store) > 0 ? `On orders over ₹${freeOver(s.store).toLocaleString("en-IN")}` : "Tracked, courier-delivered"], [ShieldCheck, "Secure Payments", "100% protected"], [BadgeCheck, "Quality Products", "Curated & verified"], [Headphones, "24×7 Support", "Always here for you"]].map(([Icon, t, d]) => (
        <div key={t} className="rounded-2xl p-5 flex items-start gap-3" style={{ background: c.surface, border: `1px solid ${border}`, boxShadow: cardShadow }}>
          <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: hexA(c.primary, 0.12), color: c.primary }}><Icon size={19} /></span>
          <div><div className="font-bold text-sm" style={{ fontFamily: font }}>{t}</div><div className="text-xs mt-0.5" style={{ color: muted }}>{d}</div></div>
        </div>
      ))}
    </div>
  </section>
  );
}

function PromoStrip({ s }) {
  const { c, font, border, muted, cardShadow } = s;
  return (
<section className="my-8" style={{ background: c.primary }}>
    <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center justify-center gap-6 flex-wrap text-sm font-semibold" style={{ color: "#fff" }}>
      <span className="flex items-center gap-2"><Tag size={15} /> Special Offers Today!</span>
      {s.coupons.slice(0, 3).map((cp) => (
        <span key={cp.code} className="px-3 py-1 rounded-full text-xs" style={{ background: "rgba(255,255,255,0.2)" }}>{cp.discount_type === "percent" ? `${cp.value}% OFF` : `₹${cp.value} OFF`} · CODE: <b>{cp.code}</b></span>
      ))}
    </div>
  </section>
  );
}

export function ProductCard({ p, s, cart }) {
  const { c, font, border, muted, cardShadow, slug, dark } = s;
  const off = p.compare_at_price ? Math.round((1 - p.price / p.compare_at_price) * 100) : 0;
  const hasVariants = p.variants?.length > 0 || p.variant_items?.length > 0;
  return (
    <div data-testid={`storefront-product-${p.id}`} className="rounded-2xl overflow-hidden group transition-transform hover:-translate-y-1 flex flex-col" style={{ background: c.surface, border: `1px solid ${border}`, boxShadow: cardShadow }}>
      <Link to={`/store/${slug}/product/${p.id}`} className="block">
        <div className="h-44 relative" style={{ background: dark ? "#0b1220" : "#f1f5f9" }}>
          {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : null}
          {off > 0 && <span className="absolute top-2 left-2 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: c.accent, color: "#fff" }}>{off}% OFF</span>}
          {p.stock <= 0 && <span className="absolute top-2 right-2 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#dc2626", color: "#fff" }}>Sold out</span>}
        </div>
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-1 mb-1"><Star size={12} fill={c.accent} color={c.accent} /><span className="text-xs font-semibold">{rating(p.id).toFixed(1)}</span><span className="text-[10px]" style={{ color: muted }}>({reviews(p.id)})</span></div>
        <Link to={`/store/${slug}/product/${p.id}`}><h3 className="font-semibold text-sm leading-tight line-clamp-1 hover:opacity-70" style={{ fontFamily: font }}>{p.name}</h3></Link>
        <div className="flex items-center gap-2 mt-1.5">{hasPriceRange(p) && <span className="text-xs" style={{ color: muted }}>From</span>}<span className="font-bold" style={{ color: c.primary }}>{inr(p.price)}</span>{p.compare_at_price ? <span className="text-xs line-through" style={{ color: muted }}>{inr(p.compare_at_price)}</span> : null}</div>
        <button onClick={() => { if (hasVariants) { window.location.href = `/store/${slug}/product/${p.id}`; } else { cart.add(p, null, 1); toast.success("Added to cart"); } }}
          data-testid={`storefront-add-to-cart-btn-${p.id}`} className="mt-3 w-full py-2 rounded-full text-sm font-bold transition-opacity hover:opacity-90" style={{ background: c.primary, color: "#fff" }}>
          {hasVariants ? "Select options" : "Add to cart"}
        </button>
      </div>
    </div>
  );
}

function SectionHead({ c, font, eyebrow, title }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest" style={{ color: c.accent }}>{eyebrow}</div>
      <h2 className="text-2xl md:text-3xl font-extrabold mt-1" style={{ fontFamily: font }}>{title}</h2>
    </div>
  );
}

export function Splash() {
  return <div className="min-h-screen bg-royal-dark flex items-center justify-center text-slate-400">Loading store…</div>;
}
export function ErrorView({ error }) {
  return (
    <div className="min-h-screen bg-royal-dark flex flex-col items-center justify-center text-slate-300 gap-3">
      <p>{error || "Store not found"}</p>
      <Link to="/" className="text-brand-yellow underline">Back to Sell24x7</Link>
    </div>
  );
}
